import { Injectable } from '@angular/core';
import { GlobalFilterService } from './global-filter.service';
import { environment } from 'src/environments/environment';
import { SharedSocket } from './shared-socket';
import { BehaviorSubject, fromEvent, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SOCKET_EVENTS } from 'src/app/core/constants/terminal-flow.constants';
import { FinanceFlowEvent, LiveViewEvent, TerminalDeviceEvent } from 'src/app/core/interfaces/terminal-flow.interface';
import { getUserProfiles } from 'src/app/core/session';

@Injectable({
    providedIn: 'root'
})
export class SocketsService {
    public socket: SharedSocket;

    // --- Finance-flow terminal streams (derived straight from the socket via fromEvent) ---
    /** Inbound messages on the finance-flow channel (payment updates, connection events). */
    public financeFlowResponse$: Observable<FinanceFlowEvent>;
    /** Inbound messages on the process-payment (pp) terminal channel — same envelope as finance-flow. */
    public terminalPaymentResponse$: Observable<FinanceFlowEvent>;
    /**
     * Inbound messages on the subscription terminal channel. Same envelope as
     * finance-flow, but the terminal reports subscription transactions on a
     * channel of their own, so the two never cross.
     */
    public subscriptionFlowResponse$: Observable<FinanceFlowEvent>;
    /** Emits when the underlying socket drops (reason string), so live flows can bail out. */
    public socketDisconnectEvent$: Observable<string>;
    /**
     * Latest state of a paired reader (`device_event`) — availability changes
     * pushed by the terminals themselves, so device pickers stay live without
     * re-fetching the list.
     */
    public terminalConnectionResponse$: Observable<TerminalDeviceEvent>;
    /**
     * Screen-by-screen mirror of what a terminal is showing the customer
     * (`live_view_frame`), so the web side can render the same screen while the
     * transaction runs.
     */
    public liveViewResponse$: Observable<LiveViewEvent>;

    /**
     * Live connectivity of the socket. Screens that depend on a reachable
     * terminal (e.g. the payment-options terminal panel) subscribe to this so
     * they can disable the option while the socket is down and re-enable it as
     * soon as it reconnects — no page refresh needed.
     */
    private readonly connectionState = new BehaviorSubject<boolean>(false);
    /**
     * Settling window before a connect/disconnect is reported. socket.io flaps
     * while it reconnects, so emitting every transition would flicker the
     * dependent UI (warning banner appearing and vanishing); only the state the
     * socket has actually settled on for this long is published.
     */
    private static readonly CONNECTION_SETTLE_MS = 1500;
    public readonly isConnected$: Observable<boolean> = this.connectionState.asObservable().pipe(
        debounceTime(SocketsService.CONNECTION_SETTLE_MS),
        distinctUntilChanged()
    );

    /** False while a secondary tab is still waiting for the shared socket's first status. */
    get isStatusResolved(): boolean {
        return this.socket.stateResolved;
    }

    /** Current connectivity, for callers that can't wait on the stream. */
    get isConnected(): boolean {
        return !!this.socket?.connected;
    }

    constructor(private globalService: GlobalFilterService) {
        const config = {
            url: `${environment.socketurl}`,
            options: {
                reconnection: true,
                reconnectionAttempts: Infinity,
                reconnectionDelay: 3000,
                reconnectionDelayMax: 5000,
                transports: ['websocket', 'polling', 'flashsocket'],
                path: environment.socket_name_space
            }
        };

        // One real connection is shared by every tab; see SharedSocket.
        this.socket = new SharedSocket(config.url, config.options);
        // Wrap the socket's own events as observables — each subscriber attaches its
        // own `.on()` listener and detaches on unsubscribe (no manual Subject bridging).
        this.financeFlowResponse$ = fromEvent<FinanceFlowEvent>(this.socket as any, SOCKET_EVENTS.FINANCE_FLOW);
        this.terminalPaymentResponse$ = fromEvent<FinanceFlowEvent>(this.socket as any, SOCKET_EVENTS.TERMINAL_PAYMENT);
        this.subscriptionFlowResponse$ = fromEvent<FinanceFlowEvent>(this.socket as any, SOCKET_EVENTS.SUBSCRIPTION_FLOW);
        this.socketDisconnectEvent$ = fromEvent<string>(this.socket as any, 'disconnect');
        this.terminalConnectionResponse$ = fromEvent<TerminalDeviceEvent>(
            this.socket as any,
            SOCKET_EVENTS.TERMINAL_CONNECTION
        );
        this.liveViewResponse$ = fromEvent<LiveViewEvent>(this.socket as any, SOCKET_EVENTS.LIVE_VIEW);

        // Connectivity is tracked here (not in OpenSocket) so it stays accurate
        // for every role, including the ones that never call OpenSocket.
        this.trackConnection();
        // After the network returns, don't wait for the next backoff tick.
        window.addEventListener('online', () => {
            if (this.socket && !this.socket.connected) {
                this.socket.connect();
            }
        });

        let userInfo = this.getUserInfo();
        if (userInfo && userInfo[0] && (userInfo[0].role_id == 4 || userInfo[0].role_id == 5 || userInfo[0].role_id == 1)) {
            this.OpenSocket();
        }
        this.SendChatMessages();
    }
    private openBound = false;
    private locationSub?: Subscription;

    /**
     * Connects (or re-connects) the socket and authenticates it. Safe to call
     * repeatedly — listeners are bound once per open/close cycle. Also called
     * after login: a logout's disconnect() is manual, so socket.io won't retry
     * on its own and the next login would otherwise stay offline until a refresh.
     */
    OpenSocket() {
        if (!this.socket) { return; }
        if (!this.openBound) {
            this.openBound = true;
            this.socket.on('connect', () => {
                console.log('Socket connected:', this.socket.id);
                this.connectToBackend();
            });
            this.socket.on('error', (err: any) => {
                console.error('Socket error:', err);
            });
            this.socket.on('disconnect', (reason: string) => {
                console.warn('Socket disconnected:', reason);
            });
            this.locationSub = this.globalService.locationFilter.subscribe((data) => {
                if (data) {
                    this.connectToBackend();
                }
            });
        }
        if (this.socket.connected) {
            this.connectToBackend();
        } else {
            this.socket.connect();
        }
    }
    closeSocket() {
        if (this.socket && (this.socket.connected || false)) {
            this.openBound = false;
            this.locationSub?.unsubscribe();
            this.socket.removeAllListeners();
            this.socket.disconnect();
            // removeAllListeners also dropped the connectivity listeners; without
            // re-attaching them the stream would never report a reconnect.
            this.trackConnection();
        }
    }

    private trackConnection() {
        this.connectionState.next(this.socket.connected);
        this.socket.on('connect', () => this.connectionState.next(true));
        this.socket.on('disconnect', () => this.connectionState.next(false));
        this.socket.on('connect_error', () => this.connectionState.next(false));
    }

    /** Emits an event on the socket, no-op if the socket isn't ready. */
    emit(event: string, payload: any) {
        if (this.socket) {
            this.socket.emit(event, payload);
        }
    }

  connectToBackend() {
    let userInfo = this.getUserInfo();
    if (userInfo && userInfo[0] && ([1,4,5].includes(userInfo[0].role_id))) {
      let payload:any = {
        access_token: localStorage.getItem('token'),
        type: "WEB",
        unique_web_token: localStorage.getItem('token')
    };

    if(userInfo?.[0].doctor_id || userInfo?.[0].default_doctor_id) {
      payload['doctor_id'] = userInfo?.[0].doctor_id || userInfo?.[0].default_doctor_id
    }

      if (userInfo[0].role_id == 1) {
        payload['enterprise_id'] = userInfo[0].enterprise_id;

        let currendDoc:any = this.globalService.getFilterObj('location');
        if (currendDoc && currendDoc.id) {
          payload['doctor_id'] = currendDoc.id;
        }
      }

      this.socket.emit('auth', payload)
      this.socket.emit('newNotification', 1);
    }
  }

  getUserInfo() {
    let userInfo = JSON.parse(localStorage.getItem('userProfileData') || "{}");
    return userInfo;
  }

    SendChatMessages() {
        setInterval(() => {
            if (this.socket && this.socket.connected) {
                if (localStorage.getItem('userProfileData')) {
                    let profile = this.getUserInfo();
                    if (profile && profile[0]) {
                        if (document.visibilityState === 'visible') {
                            this.socket.emit('window_active', {
                                url: window.location.href,
                                role_id: profile[0].role_id,
                                first_name: profile[0].doctor_first_name,
                                last_name: profile[0].doctor_last_name
                            });
                        }
                    }
                }
            }
        }, 300000)
    }
}
